import { FC, Fragment } from 'react';
import {
	Checkbox,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	InputNumber,
	Row,
	Select,
	Typography,
} from 'antd';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { TechnicalAspect } from '@/libs/fleet-api/services/types';
import {
	useSelectAddress,
	useSelectOfficeOwnershipStatus,
} from '@/libs/fleet-api/hooks/master-data';
import dayjs from 'dayjs';
import { OfficeOwnershipStatus } from '@/libs/fleet-api/enums';
import { inputCurrency } from '@/libs/antd/input-number';
import { useFBDHVisitReportApprovalPage } from './VisitReportApproval';
import { useModals } from '@/components/modals/ModalProvider';
import { FleetFile } from '@/libs/fleet-api/fetcher';

export type TechnicalAspectForm = TechnicalAspect<'payload'> &
	Pick<TechnicalAspect, 'unitDocumentFile'>;

const FBDHVisitReportDetail: FC = () => {
	const { openPreviewModal } = useModals();
	const { form, value, fieldName } = useForm<TechnicalAspectForm>();
	const { officeObservation } = useFBDHVisitReportApprovalPage();
	const { technicalAspect } = officeObservation ?? {};
	const selectAddress = useSelectAddress();
	const selectedAddress = technicalAspect?.officePostalCode ?? null;
	const selectOfficeOwnershipStatus = useSelectOfficeOwnershipStatus();

	const isSameAddress = Form.useWatch(['isOfficeAddressSameAsCustomer'], form);

	return (
		<Form
			{...ANTD_DEFAULT_PROPS.FORM}
			name="technicalAspect"
			form={form}
			labelCol={{
				span: 10,
			}}
			initialValues={
				{
					officeAddress: technicalAspect?.officeAddress,
					officeRt: technicalAspect?.officeRt,
					officeRw: technicalAspect?.officeRw,
					officePostalCodeId: technicalAspect?.officePostalCode?.postalId,
					officePhoneNo: technicalAspect?.officePhoneNo,
					officeOwnershipStatusId: technicalAspect?.officeOwnershipStatus?.id,
					officeOccupationDate: technicalAspect?.officeOccupationDate,
					officeRentalStartDate: technicalAspect?.officeRentalStartDate,
					officeRentalEndDate: technicalAspect?.officeRentalEndDate,
					officeAnnualRentalFee: technicalAspect?.officeAnnualRentalFee,
					officeHeadcount: technicalAspect?.officeHeadcount,
					startedBusinessYear: technicalAspect?.startedBusinessYear,
					previousBusiness: technicalAspect?.previousBusiness,
					companyEstablishmentDate: technicalAspect?.companyEstablishmentDate,
					branchLocation: technicalAspect?.branchLocation,
					numberCurrentBranch: technicalAspect?.numberCurrentBranch,
					unitDocumentFile: technicalAspect?.unitDocumentFile,
					isOfficeAddressSameAsCustomer:
						technicalAspect?.isOfficeAddressSameAsCustomer,
				} as TechnicalAspectForm
			}
		>
			<Row
				gutter={[20, 40]}
				align="middle"
				style={{
					marginBottom: 24,
				}}
			>
				<Col span={24}>
					<Form.Item name={fieldName('isOfficeAddressSameAsCustomer')}>
						<Checkbox
							disabled
							checked={isSameAddress}
						>
							Checklist kotak disamping apabila{' '}
							<strong style={{ color: '#1557BC' }}>Alamat Usaha</strong> juga
							merupakan{' '}
							<strong style={{ color: '#1557BC' }}>
								Alamat Perusahaan sesuai NPWP
							</strong>
						</Checkbox>
					</Form.Item>
				</Col>
			</Row>
			<Row gutter={[20, 40]}>
				<Col span={24}>
					<Row gutter={[20, 40]}>
						<Col span={24}>
							<Form.Item
								name={fieldName('officeAddress')}
								label="Alamat"
								labelCol={{ span: 5 }}
								style={{
									marginLeft: -5,
								}}
							>
								<Input disabled />
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row gutter={[20, 40]}>
						<Col span={12}>
							<Form.Item
								label="RT/RW"
								required
							>
								<Row
									wrap={false}
									gutter={8}
								>
									<Col flex="auto">
										<Form.Item
											name={fieldName('officeRt')}
											noStyle
										>
											<Input disabled />
										</Form.Item>
									</Col>
									<Col flex="none">-</Col>
									<Col flex="auto">
										<Form.Item
											name={fieldName('officeRw')}
											noStyle
										>
											<Input disabled />
										</Form.Item>
									</Col>
								</Row>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name={fieldName('officePostalCodeId')}
								label="Kode Pos"
							>
								<Select
									{...selectAddress}
									options={[
										{
											label: selectedAddress?.postalCode,
											value: selectedAddress?.postalId,
										},
									]}
									// onSelect={(value, option) => {
									// 	selectAddress.onSelect!(value, option);
									// 	setSelectedAddress(option.data);
									// }}
									disabled
								/>
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row gutter={[20, 40]}>
						<Col span={12}>
							<Form.Item label="Kelurahan">
								<Input
									disabled
									value={selectedAddress?.locationName ?? '-'}
								/>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item label="Kecamatan">
								<Input
									disabled
									value={selectedAddress?.districtName ?? '-'}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row gutter={[20, 40]}>
						<Col span={12}>
							<Form.Item label="Kota">
								<Input
									disabled
									value={selectedAddress?.cityName ?? '-'}
								/>
							</Form.Item>
						</Col>

						<Col span={12}>
							<Form.Item
								label="No. Telepon"
								name={fieldName(['officePhoneNo'])}
							>
								<Input disabled />
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row gutter={[20, 40]}>
						<Col span={12}>
							<Form.Item
								name={fieldName(['startedBusinessYear'])}
								label="Tahun Memulai Usaha"
								getValueProps={(value) => ({
									value: value && dayjs(value),
								})}
								normalize={(value) =>
									value && dayjs(value).utc(true).toISOString()
								}
							>
								<DatePicker disabled />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name={fieldName(['previousBusiness'])}
								label="Usaha Sebelumnya (Jika ada)"
							>
								<Input disabled />
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row gutter={[20, 40]}>
						<Col span={12}>
							<Form.Item
								name={fieldName(['companyEstablishmentDate'])}
								label="Tanggal Pendirian Perusahaan"
								getValueProps={(value) => ({
									value: value && dayjs(value),
								})}
								normalize={(value) =>
									value && dayjs(value).utc(true).toISOString()
								}
							>
								<DatePicker disabled />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name={fieldName(['officeHeadcount'])}
								label="Jumlah Karyawan Saat Ini"
							>
								<InputNumber disabled />
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row gutter={[20, 40]}>
						<Col span={12}>
							<Form.Item
								name={fieldName(['numberCurrentBranch'])}
								label="Jumlah Kantor Cabang Saat Ini"
							>
								<InputNumber disabled />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name={fieldName(['branchLocation'])}
								label="Lokasi Kantor Cabang"
							>
								<Input disabled />
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row
						gutter={[20, 40]}
						align="top"
					>
						<Col span={12}>
							<Form.Item
								name={fieldName(['officeOwnershipStatusId'])}
								label="Status Kepemilikan Kantor"
							>
								<Select
									{...selectOfficeOwnershipStatus}
									disabled
									options={[
										{
											label: technicalAspect?.officeOwnershipStatus.value,
											value: technicalAspect?.officeOwnershipStatus.id,
										},
									]}
								/>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item shouldUpdate>
								{({ getFieldValue }) => {
									const status = getFieldValue(
										fieldName(['officeOwnershipStatusId']),
									) as OfficeOwnershipStatus | undefined;

									if (!status) return null;

									if (status === OfficeOwnershipStatus.Sewa) {
										return (
											<Fragment>
												<Form.Item
													label="Periode Sewa"
													labelCol={{
														span: 10,
														style: { marginBottom: 20 },
													}}
												>
													<Row gutter={[20, 40]}>
														<Col span={12}>
															<Form.Item
																name={fieldName(['officeRentalStartDate'])}
																getValueProps={(value) => ({
																	value: value && dayjs(value),
																})}
																normalize={(value) =>
																	value && dayjs(value).utc(true).toISOString()
																}
																noStyle
															>
																<DatePicker
																	suffixIcon={null}
																	disabled
																/>
															</Form.Item>
														</Col>
														<Col span={12}>
															<Form.Item
																name={fieldName(['officeRentalEndDate'])}
																getValueProps={(value) => ({
																	value: value && dayjs(value),
																})}
																normalize={(value) =>
																	value && dayjs(value).utc(true).toISOString()
																}
																noStyle
															>
																<DatePicker
																	suffixIcon={null}
																	placeholder="YYYY-MM-DD"
																	disabled
																/>
															</Form.Item>
														</Col>
													</Row>
												</Form.Item>
												<Form.Item
													name={fieldName(['officeAnnualRentalFee'])}
													label="Biaya Sewa per Tahun Sebesar"
													labelCol={{
														span: 10,
														style: { marginBottom: 20 },
													}}
												>
													<InputNumber
														{...inputCurrency}
														disabled
													/>
												</Form.Item>
											</Fragment>
										);
									}

									return (
										<Form.Item
											name={fieldName(['officeOccupationDate'])}
											label="Mulai Menempati Sejak"
											getValueProps={(value) => ({
												value: value && dayjs(value),
											})}
											normalize={(value) =>
												value && dayjs(value).utc(true).toISOString()
											}
											style={{ paddingInline: 10 }}
											labelCol={{ span: 10 }}
										>
											<DatePicker.YearPicker disabled />
										</Form.Item>
									);
								}}
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Typography.Title level={3}>Unit yang Dimiliki</Typography.Title>
					<Row
						gutter={[20, 40]}
						align="middle"
					>
						<Col span={12}>
							<Form.Item
								label="Upload Dokumen Unit"
								name={fieldName(['unitDocumentFile'])}
							>
								{value?.unitDocumentFile ? (
									<a
										href="#"
										onClick={(e) => {
											e.preventDefault();
											openPreviewModal(
												value.unitDocumentFile as unknown as FleetFile,
											);
										}}
									>
										{value.unitDocumentFile.name}
									</a>
								) : (
									'-'
								)}
							</Form.Item>
						</Col>
					</Row>
				</Col>
			</Row>
			<Divider />
		</Form>
	);
};

export default FBDHVisitReportDetail;
